import React, { useState, useEffect, useCallback, useRef } from 'react';
import slugify from 'slugify';

import {
    Text,
    Container,
    Row,
    Col,
    className,
    isMobile,
} from 'aplazame-design-system';
import { default as ButtonComponent } from 'aplazame-design-system/src/components/GatsbyTransitionButtonComponent';
import { useTranslate } from 'soluble-gatsby-source';

import CardStoreComponent from '../CardStoreComponent';
import FormInput from '../FormInput';

import style from './style.module.scss';
import transitionStyle from 'aplazame-design-system/src/scss/transitions.module.scss';

const PAGE_LENGTH = 9;
const PAGE_LENGTH_MOBILE = 3;
const INPUT_SEARCH_MINLENGTH = 2;

function getStoresById(ids, stores) {
    return stores.filter((store) => ids.indexOf(store.originalId) !== -1);
}

function getStoresByTerm(term, searchTerms, stores) {
    term = slugify(term, {
        replacement: '',
        lower: true,
    });

    let ids = [];

    searchTerms.forEach((item, i) => {
        if (item.t && item.t.indexOf(term) !== -1) {
            ids.push(item.id);
        }
    });

    return getStoresById(ids, stores);
}

export default function StoreCategoriesSection({
    searchTerms,
    categories,
    stores,
    utm,
}) {
    const translate = useTranslate();
    const selectRef = useRef();
    const inputRef = useRef();
    const inputWrapperRef = useRef();
    const storesRef = useRef();

    const [blockSearchTabbing, setBlockSearchTabbing] = useState(false);
    const [initSearch, setInitSearch] = useState(true);

    const [lastCategory, setLastCategory] = useState(false);
    const [categoryNavigation, setCategoryNavigation] = useState(false);
    const [searchNavigation, setSearchNavigation] = useState(false);

    const [searchTerm, setSearchTerm] = useState(null);
    const [init, setInit] = useState(false);
    const [shownStores, setShownStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showNoResults, setShowNoResults] = useState(false);

    useEffect(() => {
        if (!inputWrapperRef || !inputWrapperRef.current) {
            return;
        }

        const listener = () => {
            setInit(true);
        };

        inputWrapperRef.current.addEventListener('sal:in', listener);
        return () => {
            inputWrapperRef.current?.removeEventListener('sal:in', listener);
        };
    }, [inputWrapperRef, setInit]);

    useEffect(() => {
        if (!init) {
            return;
        }

        setLoading(true);

        const items = storesRef.current.querySelectorAll('.sal-animate');
        for (let i = 0; i < items.length; i++) {
            items[i].style.setProperty('--sal-delay', 0);
            items[i].style.setProperty('--sal-duration', '200ms');
            items[i].classList.remove('sal-animate');
        }

        setTimeout(() => {
            if (typeof searchTerm === 'number') {
                inputRef.current.setValue('');
                setShownStores(
                    getStoresById(
                        categories[searchTerm].storeIds,
                        stores
                    ).slice(0, isMobile() ? PAGE_LENGTH_MOBILE : PAGE_LENGTH)
                );
            } else if (typeof searchTerm === 'string') {
                selectRef.current.setValue('');
                if (searchTerm && searchTerm.length >= INPUT_SEARCH_MINLENGTH) {
                    setShownStores(
                        getStoresByTerm(searchTerm, searchTerms, stores)
                    );
                } else {
                    setShownStores([]);
                }
            } else {
                inputRef.current.setValue('');
                selectRef.current.setValue(
                    translate['store-categories-section.categories.all'].text
                );
                setShownStores(
                    stores.slice(
                        0,
                        isMobile() ? PAGE_LENGTH_MOBILE : PAGE_LENGTH
                    )
                );
            }
        }, 200);
    }, [
        stores,
        categories,
        searchTerms,
        searchTerm,
        setShownStores,
        inputRef,
        storesRef,
        setLoading,
        init,
    ]);

    useEffect(() => {
        setTimeout(() => {
            const items = storesRef.current.querySelectorAll('[data-sal]');
            let itemNr = 0;

            for (let i = 0; i < items.length; i++) {
                if (!items[i].classList.contains('sal-animate')) {
                    items[i].style.setProperty(
                        '--sal-delay',
                        `${itemNr * 100}ms`
                    );
                    items[i].style.removeProperty('--sal-duration');
                    items[i].classList.add('sal-animate');
                    itemNr++;
                }
            }

            setLoading(false);
        }, 100);
    }, [shownStores, storesRef, setLoading]);

    // Handle delayed "no results" display
    useEffect(() => {
        let timeoutId;

        // Reset the no results state immediately when search term changes
        setShowNoResults(false);

        // Only show "no results" for string searches that return empty results
        if (typeof searchTerm === 'string' &&
            searchTerm.length >= INPUT_SEARCH_MINLENGTH &&
            shownStores.length === 0 &&
            !loading) {

            timeoutId = setTimeout(() => {
                setShowNoResults(true);
            }, 500); // 500ms delay - adjust as needed
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [searchTerm, shownStores, loading]);

    const onClickCategory = useCallback(
        (key) => {
            setCategoryNavigation(true);
            setLastCategory(key);
            setSearchTerm(key);
        },
        [setSearchTerm]
    );

    const onChangeSearch = useCallback(
        (e) => {
            setSearchNavigation(true);
            setSearchTerm(e.target.value ? e.target.value : null);
        },
        [setSearchTerm]
    );

    const onShowMore = useCallback(() => {
        setShownStores(
            stores.slice(
                0,
                shownStores.length +
                (isMobile() ? PAGE_LENGTH_MOBILE : PAGE_LENGTH)
            )
        );
    }, [setShownStores, shownStores, stores]);

    const onChangeSelect = useCallback(() => {
        if (selectRef && selectRef.current) {
            setTimeout(() => {
                const value = selectRef.current.getValue();
                if (value) {
                    const term = categories.findIndex(
                        (category) => category.title === value
                    );
                    setSearchTerm(term >= 0 ? term : null);
                }
            }, 0);
        }
    }, [selectRef, setSearchTerm, categories]);

    useEffect(() => {
        if (initSearch) {
            setBlockSearchTabbing(false);
            setInitSearch(false);
        } else {
            if (shownStores.length > 0 && searchTerm != null) {
                setBlockSearchTabbing(true);
            } else {
                setBlockSearchTabbing(false);
            }
        }

        // if (searchTerm == null) {
        //     setBlockSearchTabbing(false);
        // } else {
        //     if (shownStores.length > 0) {
        //         setBlockSearchTabbing(false);
        //     } else {
        //         setBlockSearchTabbing(true);
        //     }
        // }
    }, [shownStores]);

    return (
        <section>
            <Container pt={{ xs: 20 }} pb={{ xs: 20 }}>
                <Row>
                    <Col
                        col={{
                            lg: 3,
                        }}
                    >
                        <div
                            data-sal="slide-up"
                            data-sal-easing="ease-out-quart"
                            {...className('pb-4 pb-lg-0', style.wrapper)}
                            ref={inputWrapperRef}
                        >
                            <FormInput
                                ref={inputRef}
                                onChange={onChangeSearch}
                                label={
                                    translate[
                                        'store-categories-section.search.label'
                                    ].text
                                }
                                labelPosition="after"
                                onFocus={() => {
                                    setCategoryNavigation(false);
                                    setBlockSearchTabbing(false);
                                    setLastCategory(false);
                                    setSearchNavigation(false);
                                }}
                            />
                            <FormInput
                                ref={selectRef}
                                className="d-lg-none"
                                type="select"
                                onChange={onChangeSelect}
                                label={
                                    translate[
                                        'store-categories-section.categories.filter.label'
                                    ].text
                                }
                                placeholder={
                                    translate[
                                        'store-categories-section.categories.filter.label'
                                    ].text
                                }
                                options={[
                                    translate[
                                        'store-categories-section.categories.all'
                                    ].text,
                                    ...categories.map(
                                        (category) => category.title
                                    ),
                                ]}
                            />
                        </div>
                        <ul aria-label="Filtros de búsqueda" {...className(`list-unstyled d-none d-lg-block`)}>
                            <li
                                data-sal="slide-up"
                                data-sal-easing="ease-out-quart"
                            >
                                <button
                                    tabIndex={blockSearchTabbing ? lastCategory === null ? '0' : '-1' : '0'}
                                    aria-label={`Todas 1 / ${categories.length + 1} ${searchTerm === null ? 'active' : ''}`}
                                    onClick={() => {
                                        onClickCategory(null);
                                    }}
                                    onFocus={() => {
                                        setCategoryNavigation(false);
                                        setBlockSearchTabbing(false);
                                        setLastCategory(false);
                                    }}
                                    // disabled={searchTerm === null}
                                    {...className(
                                        `h4 pt-1 pb-1 mb-1 ${style['list-button']
                                        } ${searchTerm === null
                                            ? 'text-muted'
                                            : ''
                                        } ${transitionStyle.link}`
                                    )}
                                >
                                    {
                                        translate[
                                            'store-categories-section.categories.all'
                                        ].text
                                    }
                                </button>
                            </li>
                            {categories.map((category, i) => (
                                <li
                                    key={i}
                                    data-sal="slide-up"
                                    data-sal-easing="ease-out-quart"
                                >
                                    <button
                                        tabIndex={blockSearchTabbing ? lastCategory === i ? '0' : '-1' : '0'}
                                        aria-label={`${category.title} ${i + 2} / ${categories.length + 1} ${searchTerm === i ? 'active' : ''}`}
                                        onClick={() => {
                                            setBlockSearchTabbing(true);
                                            onClickCategory(i);
                                        }}
                                        onFocus={() => {
                                            setCategoryNavigation(false);
                                            setBlockSearchTabbing(false);
                                            setLastCategory(false);
                                        }}
                                        // disabled={searchTerm === i}
                                        {...className(
                                            `h4 pt-1 pb-1 mb-1 ${style['list-button']
                                            } ${searchTerm === i
                                                ? 'text-muted'
                                                : ''
                                            } ${transitionStyle.link}`
                                        )}
                                    >
                                        {category.title}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </Col>

                    <Col
                        col={{
                            lg: 9,
                        }}
                    >
                        <div id="stores-list" ref={storesRef}>
                            <Row>
                                {shownStores.length > 0 ? (
                                    <div
                                        key={`results-${shownStores.length}-${searchTerm}`}
                                        role="alert"
                                        aria-live="polite"
                                        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                                        Hay {shownStores.length} resultados
                                    </div>
                                ) : null}
                                {shownStores.length ? (
                                    shownStores.map((store, i) => (
                                        <Col
                                            key={i}
                                            col={{
                                                md: 6,
                                                lg: 4,
                                            }}
                                            pb={{ xs: 8, lg: 8 }}
                                            data-sal="slide-up"
                                            data-sal-easing="ease-out-quart"
                                        >
                                            <CardStoreComponent
                                                _onBlur={() => {
                                                    if (!categoryNavigation) {
                                                        if (!searchNavigation) {
                                                            setBlockSearchTabbing(false);
                                                        }
                                                    }
                                                }}
                                                formSearch={true}
                                                utm={utm}
                                                {...store}
                                                min={true}
                                            />
                                        </Col>
                                    ))
                                ) : typeof searchTerm === 'string' &&
                                    !loading ? (
                                    searchTerm.length <
                                        INPUT_SEARCH_MINLENGTH ? (
                                        <Col>
                                            <div role="alert">
                                                <Text
                                                    tag="p"
                                                    className="body font-italic pt-2"
                                                >
                                                    {
                                                        translate[
                                                            'store-categories-section.search.minlength'
                                                        ].text
                                                    }
                                                </Text>
                                            </div>
                                        </Col>
                                    ) : showNoResults ? (
                                        <Col className="text-center">
                                            <div role="alert">
                                                <Text
                                                    tag="p"
                                                    className="h4 pt-2 pb-6"
                                                >
                                                    {
                                                        (translate[
                                                            'store-categories-section.search.noresults'
                                                        ].text).replace('Upst! ', '')
                                                    }
                                                </Text>
                                            </div>
                                            <ButtonComponent
                                                button={{
                                                    label:
                                                        translate[
                                                            'store-categories-section.search.noresults.all'
                                                        ].text,
                                                    detached: true,
                                                    onClick: () => {
                                                        onClickCategory(null);
                                                    },
                                                    Tag: 'button',
                                                }}
                                            />
                                        </Col>
                                    ) : null
                                ) : null}
                                <Col
                                    className="text-center"
                                    pt={{ xs: 5, lg: 0 }}
                                >
                                    {typeof searchTerm === 'number' &&
                                        !loading ? (
                                        <ButtonComponent
                                            blockMobile={true}
                                            button={{
                                                detached: true,
                                                link: {
                                                    slug:
                                                        categories[searchTerm]
                                                            .slug,
                                                },
                                                label:
                                                    categories[searchTerm]
                                                        .titleAll,
                                            }}
                                        />
                                    ) : searchTerm === null &&
                                        shownStores.length &&
                                        shownStores.length < stores.length &&
                                        !loading ? (
                                        <ButtonComponent
                                            blockMobile={true}
                                            button={{
                                                detached: true,
                                                label:
                                                    typeof window !== 'undefined' ? new DOMParser().parseFromString(translate[
                                                        'store-categories-section.stores.more'
                                                    ].text, "text/html").documentElement.textContent : "",
                                                onClick: onShowMore,
                                                Tag: 'button',
                                            }}
                                        />
                                    ) : null}
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section >
    );
}
